import { isEmpty } from 'remeda'
import type { Image, Product } from '@velasca/types'
import { handleError } from './handleError'

/**
 * Retrieves the featured image for a product based on product type and settings.
 * There are special rules for "_08" and "ZX_" images.
 * If no image is found, a console.error is printed.
 *
 * @param images - An array of images for the product.
 * @param isGiftCard - A boolean indicating whether the product is a gift card.
 * @param handle - The handle of the product.
 * @param customProductType - The custom product type of the product.
 * @param customIndex - The custom index of the image to retrieve.
 * @returns The featured image for the product, or null if no image is found.
 */
export const getFeaturedImage = (
  images: Image[],
  isGiftCard: boolean,
  handle: Product['handle'],
  customProductType: Product['customProductType'],
  customIndex?: string,
): Image | null => {
  if (isEmpty(images)) {
    handleError(
      new Error(
        `Images not found on product ${handle ?? 'UNKNOWN'}, please add them inside CMS`,
      ),
    )
  }

  let image: Image | null

  if (isGiftCard || customProductType !== '3') {
    image =
      images.find(({ src }) => src.includes('_08.')) ??
      images.find(({ src }) => src.includes('ZX_')) ??
      images[0]
  } else if (customIndex) {
    const frontIndex = parseInt(customIndex)

    image = images[frontIndex - 1]
  } else {
    image = images[3]
  }

  if (isEmpty(image)) {
    handleError(
      new Error(
        `Image not found on product ${handle ?? 'UNKNOWN'}, please add it inside CMS`,
      ),
    )

    return null
  }

  return image
}
